'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Sidebar from '@/components/Sidebar';
import Toolbar from '@/components/Toolbar';

const NetworkGraph = dynamic(() => import('@/components/NetworkGraph'), { ssr: false });

export default function Page() {
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [lastScrapedAt, setLastScrapedAt] = useState(null);

  const loadAll = useCallback(async () => {
    const [g, a] = await Promise.all([
      fetch('/api/graph').then(r => r.json()),
      fetch('/api/accounts').then(r => r.json()),
    ]);
    setGraph(g);
    setAccounts(a.accounts || []);
    const latest = (a.accounts || [])
      .map(x => x.last_scraped_at)
      .filter(Boolean)
      .sort()
      .pop();
    setLastScrapedAt(latest || null);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!scraping) return;
    const t = setInterval(async () => {
      const r = await fetch('/api/scrape').then(r => r.json());
      if (!r.running) {
        setScraping(false);
        loadAll();
      }
    }, 3000);
    return () => clearInterval(t);
  }, [scraping, loadAll]);

  const edgeCounts = useMemo(() => {
    const m = {};
    for (const e of graph.edges) {
      m[e.data.source] = (m[e.data.source] || 0) + 1;
      m[e.data.target] = (m[e.data.target] || 0) + 1;
    }
    return m;
  }, [graph.edges]);

  const triggerScrape = async () => {
    setScraping(true);
    await fetch('/api/scrape', { method: 'POST' });
  };

  return (
    <div className="flex flex-col h-screen">
      <Toolbar
        onRefresh={loadAll}
        onScrape={triggerScrape}
        scraping={scraping}
        lastScrapedAt={lastScrapedAt}
        nodeCount={graph.nodes.length}
        edgeCount={graph.edges.length}
      />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          accounts={accounts}
          edgeCounts={edgeCounts}
        />
        <main className="flex-1 min-w-0 relative">
          {graph.nodes.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-graviome-muted">
              <div className="text-sm uppercase tracking-widest mb-2">No graph data yet</div>
              <div className="text-xs font-mono">Click "trigger scrape" to start collecting the mention network.</div>
            </div>
          ) : (
            <NetworkGraph
              nodes={graph.nodes}
              edges={graph.edges}
              focusId={selected}
              onNodeClick={setSelected}
              onNodeDoubleClick={async () => {
                await fetch(`/api/scrape?limit=1`, { method: 'POST' });
                setScraping(true);
              }}
            />
          )}
        </main>
      </div>
    </div>
  );
}
